import { DOCUMENT, inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class ImageService {
  readonly preloadIdLoaded = new Set<string>();

  private readonly renderer: Renderer2 = inject(RendererFactory2).createRenderer(null, null);
  private readonly document = inject(DOCUMENT);

  /**
   * Add or update a <link rel="preload" as="image"> in <head>.
   * @param imageLink Absolute or relative image URL.
   * @param id Unique DOM id for the link element.
   * @throws If imageLink or id is empty/blank.
   */
  setImagePreload(imageLink: string, id: string): void {
    if (this.preloadIdLoaded.has(id)) return;

    if (!imageLink?.trim()) {
      throw new Error(`Invalid imageLink attribute: "${imageLink}"`);
    }
    if (!id?.trim()) {
      throw new Error(`Invalid id attribute: "${id}"`);
    }

    const headElement = this.document?.head;
    if (!headElement) {
      throw new Error('Unable to access the document head.');
    }

    const newLink = this.renderer.createElement('link') as HTMLLinkElement;
    this.renderer.setAttribute(newLink, 'rel', 'preload');
    this.renderer.setAttribute(newLink, 'href', imageLink);
    this.renderer.setAttribute(newLink, 'as', 'image');
    this.renderer.setAttribute(newLink, 'id', id);
    this.renderer.appendChild(headElement, newLink);

    this.preloadIdLoaded.add(id);
  }

  /**
   * Remove multiple <link rel="preload" as="image"> by their ids.
   * - Ignores unknown/missing ids (no-op).
   * - Updates the internal Set to keep it accurate.
   * @param ids List of ids to remove.
   * @returns Number of removed elements.
   */
  removeImagePreloads(ids: readonly string[]): number {
    if (!Array.isArray(ids) || ids.length === 0) return 0;

    const head = this.document?.head;
    if (!head) return 0;

    const uniqueIds = new Set(ids.map((v) => (typeof v === 'string' ? v.trim() : '')).filter(Boolean));

    let removed = 0;
    for (const id of uniqueIds) {
      const link = this.document?.querySelector<HTMLLinkElement>(`link[id="${id}"]`);
      if (link && link.rel === 'preload' && (link as { as: string }).as === 'image') {
        this.renderer.removeChild(head, link);
        removed++;
      }
      // Keep Set in sync whether or not the node was found.
      this.preloadIdLoaded.delete(id);
    }

    return removed;
  }

  /**
   * Convenience: remove all preloads previously managed by this service.
   * @returns Number of removed elements.
   */
  clearAllImagePreloads(): number {
    if (this.preloadIdLoaded.size === 0) return 0;
    const allIds = Array.from(this.preloadIdLoaded);
    return this.removeImagePreloads(allIds);
  }
}
